// Import model
import { ObjectTask } from '../models/ObjectTask';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { ReferencedObject } from '../models/ReferencedObject';

// Import dependency serializers (for complex types)
import { ReferencedObjectSerializer } from './ReferencedObjectSerializer';

// Import derived type serializers (for polymorphic deserialization)
import { ContactTaskSerializer } from './ContactTaskSerializer';
import { ProjectTaskSerializer } from './ProjectTaskSerializer';

/**
 * Serializer for ObjectTask - handles JSON serialization and deserialization with type validation
 */
export class ObjectTaskSerializer {
  /**
   * Deserializes a JSON string into a ObjectTask instance
   */
  static deserialize(json: string): ObjectTask {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ObjectTask instance
   */
  static fromObject(obj: any): ObjectTask {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ObjectTask');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['parentObjectType'];

    switch (discriminator) {
      case 'Contact':
        return ContactTaskSerializer.fromObject(obj);
      case 'salesforce.WorkOrder':
        return ProjectTaskSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: ObjectTask = {
      assignedUserId: obj['assignedUserId'] as string | undefined,
      assignedUserIdName: obj['assignedUserId|Name'] as string | undefined,
      dueDate: obj['dueDate'] as string | undefined,
      priority: obj['priority'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      content: obj['content'] as string | undefined,
      contentType: obj['contentType'] as string | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      parent: obj['parent'] ? ReferencedObjectSerializer.fromObject(obj['parent']) : undefined as any,
      parentObjectType: obj['parentObjectType'] as string,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      parentObjectId: obj['parentObjectId'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ObjectTask instances
   */
  static deserializeArray(json: string): ObjectTask[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ObjectTask instance to a JSON string
   */
  static serialize(instance: ObjectTask): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ObjectTask instance to a plain object
   */
  static toObject(instance: ObjectTask): any {
    return {
      'assignedUserId': instance.assignedUserId,
      'assignedUserId|Name': instance.assignedUserIdName,
      'dueDate': instance.dueDate,
      'priority': instance.priority,
      'name': instance.name,
      'description': instance.description,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'lastModifiedDate': instance.lastModifiedDate,
      'content': instance.content,
      'contentType': instance.contentType,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      'isActive': instance.isActive,
      'relatedObjects': instance.relatedObjects,
      'parent': instance.parent ? ReferencedObjectSerializer.toObject(instance.parent) : undefined,
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'parentObjectId': instance.parentObjectId,
    };
  }

  /**
   * Serializes an array of ObjectTask instances to a JSON string
   */
  static serializeArray(instances: ObjectTask[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
